import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { fmtInt } from '../engine';
import { ramped, useScrollRamp } from '../motion/useScrollRamp';
import { EVENT_COLORS } from './chartTokens';

export interface StripEvent {
  id: number;
  name: string;
  date: string | null;
  contacts: number;
  responses: number;
  phase: 'live' | 'upcoming' | 'done' | 'edit';
  projectId: number | null;
  projectName: string;
  /** Cumplió algún objetivo del Plan. */
  beatPlan: boolean;
  selected: boolean;
}

export interface TagPreset {
  id: number;
  label: string;
  color: string;
  ids: number[];
}

/** Cuántos eventos dibuja la línea antes de pedir «ver todos». */
const CAP = 24;
/** Cuántos proyectos salen como preset antes del «+N más». */
const PROJECT_CAP = 4;

const same = (a: readonly number[], b: Set<number>) => a.length === b.size && a.every((x) => b.has(x));

/**
 * El selector de eventos de Insights, tal cual lo dibuja el diseño: los
 * eventos son objetos discretos sobre una línea de fechas, así que el filtro
 * ES la línea. Arriba los presets («pídeme»: todo, cada proyecto, los tres
 * últimos, los que batieron el plan) y la búsqueda; en medio una columna
 * por evento con la altura por contactos (raíz cuadrada, para que un festival
 * cinco veces mayor no aplaste al resto); debajo, la frase del alcance.
 *
 * Con muchos eventos la línea no intenta dibujar mil columnas: la búsqueda
 * acota lo que se pinta, los presets escriben en la selección, y a partir de
 * veinticuatro se pide «ver todos». La selección es un conjunto y sobrevive
 * a la búsqueda.
 */
export function EventStrip({
  events,
  tagPresets = [],
  onChange,
  sentence,
  className,
}: {
  /** Todos los eventos del alcance (marca, proyecto, fechas), en orden de fecha. */
  events: StripEvent[];
  /** Etiquetas de evento o proyecto con los eventos que las llevan; agrupan dentro y entre proyectos. */
  tagPresets?: TagPreset[];
  /** `null` = todo; un array es una selección real, incluso vacía. */
  onChange: (ids: number[] | null) => void;
  sentence: string;
  className?: string;
}) {
  const t = useT();
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [allProjects, setAllProjects] = useState(false);
  // Como el resto: las barras suben y las cifras cuentan al entrar en pantalla.
  // Los contactos llegan con los hechos, después de la lista: la rampa vuelve
  // a arrancar cuando cambian.
  const { ref: rampRef, p } = useScrollRamp(800, events.reduce((s, e) => s + e.contacts, 0));

  const selected = useMemo(() => new Set(events.filter((e) => e.selected).map((e) => e.id)), [events]);
  const everything = selected.size === events.length;

  const projects = useMemo(() => {
    const by = new Map<number, { id: number; name: string; ids: number[]; recency: number }>();
    events.forEach((e, i) => {
      if (e.projectId == null) return;
      const cur = by.get(e.projectId) ?? { id: e.projectId, name: e.projectName, ids: [], recency: -1 };
      cur.ids.push(e.id);
      cur.recency = i;
      by.set(e.projectId, cur);
    });
    return [...by.values()].sort((a, b) => b.recency - a.recency);
  }, [events]);
  const shownProjects = allProjects ? projects : projects.slice(0, PROJECT_CAP);
  const hiddenProjects = projects.length - shownProjects.length;

  const presets: Array<{ key: string; label: string; hint: string; set: number[] | null; project?: boolean; color?: string }> = [
    { key: 'all', label: t('insights.picker.everything'), hint: t('insights.picker.events', { n: events.length }), set: null, project: true },
    ...shownProjects.map((p) => ({ key: `p${p.id}`, label: p.name, hint: t('insights.picker.events', { n: p.ids.length }), set: p.ids, project: true })),
    // Las etiquetas son la capa de agrupación: sin un nivel formal de grupo,
    // una etiqueta es cómo un conjunto de eventos pasa a ser algo sobre lo
    // que se puede informar.
    ...tagPresets.map((tg) => ({ key: `t${tg.id}`, label: tg.label, hint: t('insights.picker.events', { n: tg.ids.length }), set: tg.ids, color: tg.color })),
    { key: 'last3', label: t('insights.picker.lastThree'), hint: t('insights.picker.mostRecent'), set: events.filter((e) => e.phase !== 'upcoming').slice(-3).map((e) => e.id) },
    { key: 'beat', label: t('insights.picker.beatPlan'), hint: t('insights.picker.overGoal'), set: events.filter((e) => e.beatPlan).map((e) => e.id) },
  ];

  const needle = q.trim().toLowerCase();
  const matches = useMemo(
    () =>
      needle
        ? events.filter((e) => `${e.name} ${e.projectName} ${e.date ? dayjs(e.date).format('MMM D YYYY') : ''}`.toLowerCase().includes(needle))
        : events,
    [events, needle],
  );
  const shown = expanded ? matches : matches.slice(-CAP);
  const hidden = matches.length - shown.length;
  const max = Math.max(1, ...events.map((e) => e.contacts));

  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next.size === events.length ? null : events.filter((e) => next.has(e.id)).map((e) => e.id));
  };

  const phases: Array<StripEvent['phase']> = ['done', 'live', 'upcoming'];
  const colorOf = (p: StripEvent['phase']) => (p === 'live' ? EVENT_COLORS.live : p === 'upcoming' ? EVENT_COLORS.upcoming : EVENT_COLORS.done);

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {/* Presets */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="dash-mono pr-1 text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: 'var(--color-dash-fg-muted)' }}>{t('insights.picker.askFor')}</span>
        {presets.map((p) => {
          const on = p.set === null ? everything : !everything && same(p.set, selected);
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => onChange(p.set)}
              className="flex items-center gap-2 rounded-full border px-3.5 py-2 text-[13px] transition-colors"
              style={{
                background: on ? 'var(--color-dash-ink)' : p.project ? 'var(--color-dash-paper-3)' : 'var(--color-dash-paper)',
                color: on ? 'var(--color-dash-lime)' : 'var(--color-dash-fg-2)',
                borderColor: on ? 'var(--color-dash-ink)' : p.project ? 'var(--color-dash-border-2)' : 'var(--color-dash-border)',
                fontWeight: p.project ? 700 : 500,
              }}
            >
              {p.color && <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />}
              {p.label}
              <span className="dash-mono text-[9px] font-bold uppercase tracking-[0.06em]" style={{ color: on ? 'var(--color-dash-night-fg-subtle)' : 'var(--color-dash-fg-subtle)' }}>{p.hint}</span>
            </button>
          );
        })}
        {hiddenProjects > 0 && (
          <button type="button" onClick={() => setAllProjects(true)} className="text-xs font-semibold hover:underline" style={{ color: 'var(--color-dash-olive)' }}>
            {t('insights.picker.moreProjects', { n: hiddenProjects })}
          </button>
        )}
      </div>

      {/* Búsqueda */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[260px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: 'var(--color-dash-fg-subtle)' }} />
          <input
            type="text"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setExpanded(false);
            }}
            placeholder={t('insights.picker.search', { n: events.length })}
            className="h-10 w-full rounded-[10px] border bg-transparent pl-9 pr-9 text-sm focus:outline-none"
            style={{ borderColor: needle ? 'var(--color-dash-lime)' : 'var(--color-dash-border-2)', color: 'var(--color-dash-fg)' }}
          />
          {needle && (
            <button type="button" onClick={() => setQ('')} aria-label="×" className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-0.5" style={{ color: 'var(--color-dash-fg-subtle)' }}>
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <button type="button" onClick={() => onChange(matches.length === events.length ? null : matches.map((e) => e.id))} className="text-[13px] font-semibold hover:underline" style={{ color: 'var(--color-dash-olive)' }}>
          {t('insights.picker.selectMatching', { n: matches.length })}
        </button>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex flex-wrap items-center gap-4">
          <span className="dash-mono text-[10px] font-bold uppercase tracking-[0.08em]" style={{ color: 'var(--color-dash-fg-muted)' }}>{t('insights.picker.match', { n: matches.length, total: events.length })}</span>
          {phases.map((ph) => (
            <span key={ph} className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--color-dash-fg-2)' }}>
              <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: colorOf(ph) }} />
              {t(`track.group.${ph}`)}
            </span>
          ))}
        </span>
        <span className="text-xs" style={{ color: 'var(--color-dash-fg-subtle)' }}>{t('insights.picker.caption')}</span>
      </div>

      {/* La línea */}
      {matches.length === 0 ? (
        <p className="py-6 text-center text-sm" style={{ color: 'var(--color-dash-fg-subtle)' }}>{t('insights.picker.noMatch')}</p>
      ) : (
        <div ref={rampRef} className="overflow-x-auto pb-2 [scrollbar-width:thin]" role="group">
          <div className="flex min-w-full gap-2" style={{ width: `max(100%, ${shown.length * 128}px)` }}>
            {shown.map((e) => {
              const on = e.selected;
              const h = 10 + Math.sqrt(e.contacts / max) * 62 * p;
              return (
                <button
                  key={e.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggle(e.id)}
                  title={`${e.name} · ${fmtInt(e.contacts)} ${t('dash.strip.contacts')}`}
                  className="flex min-w-[118px] flex-1 basis-0 flex-col items-center gap-1 rounded-lg px-1 pb-1.5 pt-2 text-center transition-colors hover:bg-[var(--color-dash-paper-2)]"
                >
                  <span className="dash-mono text-[11px] font-bold tabular-nums" style={{ color: on ? 'var(--color-dash-fg)' : 'var(--color-dash-fg-faint)' }}>{fmtInt(ramped(e.contacts, p))}</span>
                  <span className="relative flex h-[76px] w-full items-end">
                    <span className="w-full rounded-t-[6px] transition-[height,background-color] duration-300" style={{ height: `${h}px`, background: on ? colorOf(e.phase) : 'var(--color-dash-border)' }} />
                    {e.phase === 'live' && <span className="absolute right-1 top-0 h-2 w-2 animate-pulse rounded-full" style={{ background: 'var(--color-dash-lime)' }} />}
                  </span>
                  <span className="w-full truncate text-[12.5px] font-bold" style={{ color: on ? 'var(--color-dash-fg)' : 'var(--color-dash-fg-faint)' }}>{e.name}</span>
                  <span className="dash-mono text-[10px]" style={{ color: on ? 'var(--color-dash-fg-muted)' : '#c6c6c0' }}>{e.date ? dayjs(e.date).format('MMM D') : '—'}</span>
                  <span className="dash-mono text-[10px] font-bold" style={{ color: on ? 'var(--color-dash-olive)' : '#c6c6c0' }}>
                    {e.responses ? t('dash.strip.responses', { n: fmtInt(e.responses) }) : t('dash.strip.noSurvey')}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm" style={{ color: 'var(--color-dash-fg-2)' }}>
        <span>{sentence}</span>
        <span className="flex items-center gap-3 text-xs font-semibold">
          {hidden > 0 && (
            <button type="button" className="hover:underline" style={{ color: 'var(--color-dash-olive)' }} onClick={() => setExpanded(true)}>
              {t('insights.picker.more', { n: hidden })}
            </button>
          )}
          {!everything && (
            <button type="button" className="hover:underline" style={{ color: 'var(--color-dash-olive)' }} onClick={() => onChange(null)}>
              {t('insights.scope.everything')}
            </button>
          )}
        </span>
      </div>
    </div>
  );
}
